/*
  Cart shipping estimator.

  Calls Shopify's own /cart/shipping_rates.json, so these are the live carrier quotes the
  customer will be charged rather than a guess. That endpoint wants a country and province
  alongside the ZIP; for a US ZIP the province can be omitted and Shopify resolves it, but
  it returns 422 with a validation body when it cannot, so that case is reported honestly
  instead of being swallowed into "no rates available".

  Listens on the document, not on one form: the estimate is on the cart page and in the cart
  drawer, and the drawer's markup is replaced every time the cart changes. The state and ZIP
  last used are kept for the visit and filled in when another estimate is opened.
*/
(() => {
  if (window.atlasShippingEstimate) return;
  window.atlasShippingEstimate = true;

  const KEY = 'atlas-ship-address';
  const money = (v) => Number(v).toLocaleString(undefined, { style: 'currency', currency: 'USD' });

  // Shopify returns the price in dollars as a string here, unlike almost every other
  // endpoint, which returns cents as an integer. Mixing the two shows $446 for $4.46.
  const priceOf = (rate) => parseFloat(rate.price);

  const parts = (root) => ({
    zipEl: root.querySelector('[data-atlas-ship-zip]'),
    stateEl: root.querySelector('[data-atlas-ship-state]'),
    goEl: root.querySelector('[data-atlas-ship-go]'),
    statusEl: root.querySelector('[data-atlas-ship-status]'),
    listEl: root.querySelector('[data-atlas-ship-rates]'),
    noteEl: root.querySelector('[data-atlas-ship-note]'),
  });

  const remembered = () => {
    try { return JSON.parse(sessionStorage.getItem(KEY)) || {}; } catch (e) { return {}; }
  };

  async function estimate(root) {
    const { zipEl, stateEl, goEl, statusEl, listEl, noteEl } = parts(root);
    const zip = (zipEl.value || '').trim();
    const state = (stateEl.value || '').trim();
    if (!zip || !state) {
      statusEl.textContent = 'Choose a state and enter a ZIP code.';
      statusEl.className = 'atlas-ship__status is-error';
      return;
    }
    try { sessionStorage.setItem(KEY, JSON.stringify({ zip, state })); } catch (e) {}

    goEl.setAttribute('disabled', 'disabled');
    statusEl.textContent = 'Getting rates…';
    statusEl.className = 'atlas-ship__status';
    listEl.hidden = true;
    noteEl.hidden = true;

    const qs = new URLSearchParams();
    qs.set('shipping_address[zip]', zip);
    qs.set('shipping_address[country]', 'United States');
    // Required. Without it the endpoint returns {"province":["Select a state"]}.
    qs.set('shipping_address[province]', state);

    try {
      const res = await fetch(`/cart/shipping_rates.json?${qs.toString()}`, {
        headers: { Accept: 'application/json' },
      });
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        // The validation body is keyed by field (zip, province, country), so report
        // whatever Shopify actually objected to rather than guessing it was the ZIP.
        const detail = data && typeof data === 'object'
          ? Object.values(data).flat().join(' ')
          : '';
        throw new Error(detail || 'We could not price that address.');
      }

      const rates = (data && (data.shipping_rates || data.shipping_shipping_rates)) || [];
      if (!rates.length) {
        statusEl.textContent = 'No rates came back for that ZIP. Contact us and we will quote it.';
        statusEl.className = 'atlas-ship__status is-error';
        return;
      }

      rates.sort((a, b) => priceOf(a) - priceOf(b));
      listEl.innerHTML = rates
        .slice(0, 5)
        .map((r, i) => {
          // The carrier name often carries a delivery estimate in brackets; split it out
          // so the service and the date are not one long run of text.
          const m = r.name.match(/^(.*?)\s*\((.*)\)\s*$/);
          const name = m ? m[1] : r.name;
          const when = m ? m[2] : '';
          return `<li class="atlas-ship__rate${i === 0 ? ' is-cheapest' : ''}">
              <span class="atlas-ship__rate-name">${name}${
            when ? `<span class="atlas-ship__rate-when">${when}</span>` : ''
          }</span>
              <span class="atlas-ship__rate-price">${money(priceOf(r))}</span>
            </li>`;
        })
        .join('');
      listEl.hidden = false;
      noteEl.hidden = false;
      statusEl.textContent = '';
      statusEl.className = 'atlas-ship__status';
    } catch (err) {
      statusEl.textContent = err.message;
      statusEl.className = 'atlas-ship__status is-error';
    } finally {
      goEl.removeAttribute('disabled');
    }
  }

  const rootOf = (el) => el && el.closest && el.closest('[data-atlas-shipping]');

  document.addEventListener('click', (e) => {
    const go = e.target.closest && e.target.closest('[data-atlas-ship-go]');
    if (go && rootOf(go)) estimate(rootOf(go));
  });
  // Caught on the way down and kept here: in the drawer the estimate sits inside the cart
  // items, whose change listener would read any change as a new quantity.
  document.addEventListener('change', (e) => {
    const root = rootOf(e.target);
    if (!root) return;
    e.stopPropagation();
    if (e.target.matches('[data-atlas-ship-state]') && parts(root).zipEl.value.trim()) estimate(root);
  }, true);
  document.addEventListener('keydown', (e) => {
    const root = rootOf(e.target);
    if (root && e.key === 'Enter' && e.target.matches('[data-atlas-ship-zip]')) {
      e.preventDefault();
      estimate(root);
    }
  });
  // An estimate opened after one was made elsewhere starts with the same address.
  document.addEventListener('toggle', (e) => {
    const root = e.target;
    if (!root.matches || !root.matches('[data-atlas-shipping]') || !root.open) return;
    const { zipEl, stateEl } = parts(root);
    const last = remembered();
    if (!zipEl.value && last.zip) zipEl.value = last.zip;
    if (!stateEl.value && last.state) stateEl.value = last.state;
  }, true);
})();
